import React, { useEffect, useState } from "react";
import { toast } from "sonner";

// Bare-bones admin console. Reachable only at `/admin`. Two screens:
//   1. Sign in (email + admin PIN) — POST /api/auth/admin/login
//   2. License management:
//        - list all licenses            GET  /api/auth/admin/licenses
//        - issue a new license          POST /api/auth/admin/licenses/issue
//        - revoke an existing license   POST /api/auth/admin/licenses/{id}/revoke
//
// Admin session token lives in sessionStorage (NOT localStorage) so it's
// wiped when the tab closes — extra safety since this UI can issue new
// PINs and revoke licenses.

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "pa_admin_session_token";

function getAdminToken() {
  try { return sessionStorage.getItem(TOKEN_KEY) || ""; } catch { return ""; }
}
function saveAdminToken(t) {
  try { sessionStorage.setItem(TOKEN_KEY, t); } catch { /* noop */ }
}
function clearAdminToken() {
  try { sessionStorage.removeItem(TOKEN_KEY); } catch { /* noop */ }
}

async function api(path, opts = {}) {
  const token = getAdminToken();
  const res = await fetch(`${BACKEND}${path}`, {
    ...opts,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(opts.headers || {}),
    },
  });
  const text = await res.text();
  let json;
  try { json = text ? JSON.parse(text) : {}; } catch { json = { raw: text }; }
  if (!res.ok) {
    const err = new Error(String(json.detail || json.error || `HTTP_${res.status}`));
    err.status = res.status;
    throw err;
  }
  return json;
}

export default function AdminApp() {
  const [signedIn, setSignedIn] = useState(!!getAdminToken());
  const [showChangePin, setShowChangePin] = useState(false);

  return (
    <div className="ad-root">
      <style>{styles}</style>
      <div className="ad-topbar">
        <div className="ad-brand">
          Pharmacy <em>Analyzer</em> · <span className="ad-pill">Admin</span>
        </div>
        {signedIn && (
          <div className="ad-top-actions">
            <button
              type="button"
              className="ad-secondary"
              onClick={() => setShowChangePin(true)}
              data-testid="admin-change-pin-btn"
            >
              Change PIN
            </button>
            <button
              type="button"
              className="ad-signout"
              onClick={() => { clearAdminToken(); setSignedIn(false); }}
              data-testid="admin-signout-btn"
            >
              Sign out
            </button>
          </div>
        )}
      </div>

      {signedIn ? (
        <>
          <AdminConsole onUnauthorized={() => { clearAdminToken(); setSignedIn(false); }} />
          {showChangePin && (
            <ChangePinModal
              onClose={() => setShowChangePin(false)}
              onSignOut={() => { clearAdminToken(); setSignedIn(false); }}
            />
          )}
        </>
      ) : (
        <AdminLogin onSignedIn={() => setSignedIn(true)} />
      )}
    </div>
  );
}

function AdminLogin({ onSignedIn }) {
  const [email, setEmail] = useState("");
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function submit(e) {
    e.preventDefault();
    if (!email.trim() || !pin.trim() || busy) return;
    setBusy(true);
    setError("");
    try {
      const r = await api("/api/auth/admin/login", {
        method: "POST",
        body: JSON.stringify({ email: email.trim(), pin: pin.trim() }),
      });
      saveAdminToken(r.admin_session_token);
      onSignedIn();
    } catch (err) {
      if (err.status === 401) setError("Invalid email or admin PIN.");
      else setError(String(err.message || err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="ad-panel ad-login" data-testid="admin-login-form">
      <div className="ad-h1">Admin sign in</div>
      <label className="ad-field">
        <span className="ad-label">Email</span>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="username"
          autoCapitalize="none"
          autoFocus
          disabled={busy}
          className="ad-input"
          data-testid="admin-email-input"
        />
      </label>
      <label className="ad-field">
        <span className="ad-label">Admin PIN</span>
        <input
          type="password"
          value={pin}
          onChange={(e) => setPin(e.target.value)}
          autoComplete="current-password"
          disabled={busy}
          className="ad-input"
          data-testid="admin-pin-input"
        />
      </label>
      <button
        type="submit"
        className="ad-primary"
        disabled={busy || !email.trim() || !pin.trim()}
        data-testid="admin-signin-btn"
      >
        {busy ? "Signing in…" : "Sign in"}
      </button>
      {error ? <div className="ad-error" data-testid="admin-login-error">{error}</div> : null}
    </form>
  );
}

function AdminConsole({ onUnauthorized }) {
  const [licenses, setLicenses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("");
  // One-time PIN reveal after issuing a new license — never fetched again.
  const [issuedPin, setIssuedPin] = useState(null);

  async function reload() {
    setLoading(true);
    setError("");
    try {
      const r = await api("/api/auth/admin/licenses");
      setLicenses(r.licenses || []);
    } catch (err) {
      if (err.status === 401) return onUnauthorized();
      setError(String(err.message || err));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { reload(); }, []);

  async function issueLicense(payload) {
    try {
      const r = await api("/api/auth/admin/licenses/issue", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      setIssuedPin({
        pin: r.pin,
        license_id: r.license_id,
        pharmacy_name: r.pharmacy_name,
      });
      reload();
    } catch (err) {
      if (err.status === 401) return onUnauthorized();
      toast.error(String(err.message || err));
    }
  }

  async function revoke(license_id, pharmacy_name) {
    if (!window.confirm(`Revoke license for ${pharmacy_name}?\n\nThis will disable Face ID pairing for their device. All legacy data (payment history, contact info, subscription record) is preserved — you can reinstate later.`)) return;
    try {
      await api(`/api/auth/admin/licenses/${license_id}/revoke`, { method: "POST" });
      toast.success(`Revoked ${pharmacy_name}`);
      reload();
    } catch (err) {
      if (err.status === 401) return onUnauthorized();
      toast.error(String(err.message || err));
    }
  }

  async function reinstate(license_id, pharmacy_name) {
    if (!window.confirm(
      `Reinstate ${pharmacy_name}?\n\n` +
      `This restores access and issues a FRESH PIN. The old PIN and any ` +
      `previously-paired device are permanently invalidated — the ` +
      `pharmacy will need to pair again with the new PIN.`
    )) return;
    try {
      const r = await api(`/api/auth/admin/licenses/${license_id}/reinstate`,
        { method: "POST" });
      // Reuse the one-time reveal modal used when issuing a brand-new
      // license — the UX is identical (fresh PIN, one-time display).
      setIssuedPin({
        pin: r.pin,
        license_id: r.license_id,
        pharmacy_name: r.pharmacy_name,
      });
      reload();
    } catch (err) {
      if (err.status === 401) return onUnauthorized();
      toast.error(String(err.message || err));
    }
  }

  async function deleteLicense(license_id, pharmacy_name) {
    // Two-step confirm — a general warning, then require typing the
    // pharmacy name so a mis-click can never wipe the wrong row.
    // Prefer Revoke for anything you might want back — Delete is
    // irreversible and drops payment history + signup + license rows.
    if (!window.confirm(
      `Permanently delete ${pharmacy_name}?\n\n` +
      `This removes the license, any linked signup record, and payment ` +
      `history from the database. THIS CANNOT BE UNDONE.\n\n` +
      `If you might ever want them back, use Revoke instead.\n\n` +
      `Click OK to continue to the final confirmation.`
    )) return;
    const typed = window.prompt(
      `Type the pharmacy name exactly to confirm deletion:\n\n${pharmacy_name}`,
      ""
    );
    if (typed == null) return;
    if (typed.trim() !== String(pharmacy_name).trim()) {
      toast.error("Name did not match — deletion cancelled.");
      return;
    }
    try {
      await api(`/api/auth/admin/licenses/${license_id}`, { method: "DELETE" });
      toast.success(`Deleted ${pharmacy_name}`);
      reload();
    } catch (err) {
      if (err.status === 401) return onUnauthorized();
      toast.error(String(err.message || err));
    }
  }

  const filtered = filter.trim()
    ? licenses.filter((l) => {
        const q = filter.toLowerCase();
        return [l._id, l.pharmacy_name, l.contact_email, l.status]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q));
      })
    : licenses;

  return (
    <>
      {issuedPin && (
        <IssuedPinModal
          {...issuedPin}
          onClose={() => setIssuedPin(null)}
        />
      )}
      <div className="ad-panel">
        <IssueForm onIssue={issueLicense} />
      </div>

      <div className="ad-panel">
        <div className="ad-row ad-row-header">
          <div className="ad-h2">Licenses ({licenses.length})</div>
          <div className="ad-actions">
            <input
              type="search"
              placeholder="Filter by name / email / status"
              className="ad-search"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              data-testid="admin-filter-input"
            />
            <button className="ad-secondary" onClick={reload} disabled={loading}
              data-testid="admin-reload-btn">
              {loading ? "…" : "Refresh"}
            </button>
          </div>
        </div>

        {error ? <div className="ad-error">{error}</div> : null}

        <div className="ad-table" data-testid="admin-license-table">
          <div className="ad-thead">
            <div>Pharmacy</div>
            <div>Email</div>
            <div>Status</div>
            <div>Created</div>
            <div></div>
          </div>
          {filtered.length === 0 && !loading && (
            <div className="ad-empty">No licenses match.</div>
          )}
          {filtered.map((l) => (
            <div className="ad-trow" key={l._id} data-testid={`admin-license-row-${l._id}`}>
              <div>
                <div className="ad-name">{l.pharmacy_name || "—"}</div>
                <div className="ad-id">{l._id}</div>
              </div>
              <div data-label="Email" className="ad-truncate">{l.contact_email || "—"}</div>
              <div data-label="Status">
                <StatusBadge status={l.status} pinUsed={l.pin_used} />
              </div>
              <div data-label="Created" className="ad-date">{fmtDate(l.created_at)}</div>
              <div className="ad-rowactions">
                {l.status === "revoked" ? (
                  <button
                    className="ad-primary-link"
                    onClick={() => reinstate(l._id, l.pharmacy_name || l._id)}
                    data-testid={`admin-reinstate-btn-${l._id}`}
                    title="Restore access and issue a fresh PIN"
                  >
                    Reinstate
                  </button>
                ) : (
                  <button
                    className="ad-danger-link"
                    onClick={() => revoke(l._id, l.pharmacy_name || l._id)}
                    data-testid={`admin-revoke-btn-${l._id}`}
                    title="Disable access — legacy data is preserved"
                  >
                    Revoke
                  </button>
                )}
                <button
                  className="ad-danger-link ad-danger-link-strong"
                  onClick={() => deleteLicense(l._id, l.pharmacy_name || l._id)}
                  data-testid={`admin-delete-btn-${l._id}`}
                  title="Permanently remove from the database — cannot be undone"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

function IssueForm({ onIssue }) {
  const [pharmacy_name, setName] = useState("");
  const [contact_email, setEmail] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(e) {
    e.preventDefault();
    if (!pharmacy_name.trim() || busy) return;
    setBusy(true);
    try {
      await onIssue({
        pharmacy_name: pharmacy_name.trim(),
        contact_email: contact_email.trim() || null,
        notes: notes.trim() || null,
      });
      setName("");
      setEmail("");
      setNotes("");
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="ad-issue" data-testid="admin-issue-form">
      <div className="ad-h2">Issue new license</div>
      <div className="ad-issue-grid">
        <label className="ad-field">
          <span className="ad-label">Pharmacy name *</span>
          <input
            value={pharmacy_name}
            onChange={(e) => setName(e.target.value)}
            disabled={busy}
            className="ad-input"
            data-testid="admin-issue-name-input"
          />
        </label>
        <label className="ad-field">
          <span className="ad-label">Contact email</span>
          <input
            type="email"
            value={contact_email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={busy}
            className="ad-input"
            data-testid="admin-issue-email-input"
          />
        </label>
        <label className="ad-field ad-field-full">
          <span className="ad-label">Notes</span>
          <input
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            disabled={busy}
            placeholder="e.g. manual recovery for quarantined email"
            className="ad-input"
            data-testid="admin-issue-notes-input"
          />
        </label>
      </div>
      <button
        type="submit"
        className="ad-primary"
        disabled={busy || !pharmacy_name.trim()}
        data-testid="admin-issue-btn"
      >
        {busy ? "Issuing…" : "Issue license"}
      </button>
    </form>
  );
}

function IssuedPinModal({ pin, license_id, pharmacy_name, onClose }) {
  const [copied, setCopied] = useState(false);
  function copy() {
    try {
      navigator.clipboard.writeText(pin);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* noop */ }
  }
  return (
    <div className="ad-modal" data-testid="admin-issued-pin-modal" onClick={onClose}>
      <div className="ad-modal-inner" onClick={(e) => e.stopPropagation()}>
        <div className="ad-h2">New license issued</div>
        <div className="ad-modal-sub">
          Give this PIN to <strong>{pharmacy_name}</strong>. It is displayed
          <strong> once</strong> — after closing this window there is no way
          to retrieve it again.
        </div>
        <div className="ad-pin-box">
          <div className="ad-pin-label">PIN</div>
          <div className="ad-pin-value" data-testid="admin-issued-pin-value">{pin}</div>
          <button className="ad-copy-btn" onClick={copy}
            data-testid="admin-issued-pin-copy-btn">
            {copied ? "✓ Copied" : "Copy PIN"}
          </button>
        </div>
        <div className="ad-modal-meta">
          License ID: <code>{license_id}</code>
        </div>
        <button className="ad-primary" onClick={onClose}
          data-testid="admin-issued-pin-close-btn">
          I&rsquo;ve saved it, close
        </button>
      </div>
    </div>
  );
}

function StatusBadge({ status, pinUsed }) {
  const label = status === "revoked" ? "revoked"
    : (pinUsed ? "paired" : (status || "pending"));
  const cls = status === "revoked" ? "ad-badge-err"
    : (pinUsed ? "ad-badge-ok" : "ad-badge-warn");
  return <span className={`ad-badge ${cls}`}>{label}</span>;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-CA",
      { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return "—";
  }
}

function ChangePinModal({ onClose, onSignOut }) {
  const [currentPin, setCurrentPin] = useState("");
  const [newPin, setNewPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  async function submit(e) {
    e.preventDefault();
    if (busy) return;
    setError("");
    if (newPin !== confirmPin) {
      setError("New PIN and confirmation don't match.");
      return;
    }
    if (newPin.length < 8) {
      setError("New PIN must be at least 8 characters.");
      return;
    }
    if (newPin === currentPin) {
      setError("New PIN must differ from the current one.");
      return;
    }
    setBusy(true);
    try {
      await api("/api/auth/admin/change-pin", {
        method: "POST",
        body: JSON.stringify({ current_pin: currentPin, new_pin: newPin }),
      });
      setDone(true);
    } catch (err) {
      if (err.status === 401) {
        const d = String(err.message || "");
        if (d.includes("INVALID_CURRENT_PIN")) {
          setError("Current PIN is incorrect.");
        } else {
          // Session died mid-request.
          onSignOut();
        }
      } else {
        setError(String(err.message || err));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="ad-modal" data-testid="admin-change-pin-modal" onClick={onClose}>
      <div className="ad-modal-inner" onClick={(e) => e.stopPropagation()}>
        {done ? (
          <>
            <div className="ad-h2">PIN updated</div>
            <div className="ad-modal-sub">
              Your new admin PIN is now active. Please save it somewhere safe —
              there is no recovery flow. If you forget it, the only way back in
              is via a backend/database reset.
            </div>
            <button className="ad-primary" onClick={onClose}
              data-testid="admin-change-pin-close-done-btn">
              Done
            </button>
          </>
        ) : (
          <form onSubmit={submit}>
            <div className="ad-h2">Change admin PIN</div>
            <div className="ad-modal-sub">
              Rotate the seed PIN to something private. Minimum 8 characters.
            </div>
            <label className="ad-field">
              <span className="ad-label">Current PIN</span>
              <input
                type="password"
                value={currentPin}
                onChange={(e) => setCurrentPin(e.target.value)}
                className="ad-input"
                disabled={busy}
                autoFocus
                data-testid="admin-change-current-pin-input"
              />
            </label>
            <label className="ad-field">
              <span className="ad-label">New PIN (min 8 chars)</span>
              <input
                type="password"
                value={newPin}
                onChange={(e) => setNewPin(e.target.value)}
                className="ad-input"
                disabled={busy}
                data-testid="admin-change-new-pin-input"
              />
            </label>
            <label className="ad-field">
              <span className="ad-label">Confirm new PIN</span>
              <input
                type="password"
                value={confirmPin}
                onChange={(e) => setConfirmPin(e.target.value)}
                className="ad-input"
                disabled={busy}
                data-testid="admin-change-confirm-pin-input"
              />
            </label>
            <button
              type="submit"
              className="ad-primary"
              disabled={busy || !currentPin || !newPin || !confirmPin}
              data-testid="admin-change-pin-submit-btn"
            >
              {busy ? "Updating…" : "Update PIN"}
            </button>
            <button
              type="button"
              className="ad-secondary"
              onClick={onClose}
              disabled={busy}
              style={{ marginTop: 8, width: "100%" }}
              data-testid="admin-change-pin-cancel-btn"
            >
              Cancel
            </button>
            {error ? <div className="ad-error">{error}</div> : null}
          </form>
        )}
      </div>
    </div>
  );
}

const styles = `
.ad-root {
  min-height: 100vh;
  background: #F7F3EC;
  color: #1B3B2E;
  font-family: 'Geist','Inter',sans-serif;
  padding: 20px 24px 60px;
  max-width: 1080px;
  margin: 0 auto;
}
.ad-topbar {
  display: flex; justify-content: space-between; align-items: center;
  padding: 8px 0 20px;
  border-bottom: 1px solid rgba(27,59,46,0.10);
  margin-bottom: 24px;
}
.ad-brand {
  font-family: 'Fraunces', serif;
  font-size: 18px;
  color: #1B3B2E;
}
.ad-brand em { color: #2B5E4A; font-style: italic; }
.ad-pill {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 100px;
  background: #1B3B2E;
  color: #FBF6E9;
  font-family: 'Geist','Inter',sans-serif;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  margin-left: 4px;
}
.ad-signout {
  background: transparent;
  border: 1px solid rgba(27,59,46,0.28);
  padding: 6px 14px;
  border-radius: 100px;
  color: #1B3B2E;
  font-family: inherit;
  font-size: 12px;
  cursor: pointer;
}
.ad-signout:hover { background: rgba(27,59,46,0.06); }
.ad-top-actions {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
}

.ad-panel {
  background: #FFFDF7;
  border: 1px solid rgba(27,59,46,0.10);
  border-radius: 14px;
  padding: 22px 26px;
  margin-bottom: 18px;
}
.ad-login { max-width: 380px; margin: 60px auto; }

.ad-h1 {
  font-family: 'Fraunces', serif;
  font-size: 22px;
  margin-bottom: 20px;
}
.ad-h2 {
  font-family: 'Fraunces', serif;
  font-size: 17px;
  margin-bottom: 14px;
}

.ad-field { display: flex; flex-direction: column; margin-bottom: 12px; }
.ad-field-full { grid-column: 1 / -1; }
.ad-label {
  font-size: 10px;
  color: #5D4A28;
  margin-bottom: 5px;
  text-transform: uppercase;
  letter-spacing: 0.9px;
  font-weight: 500;
}
.ad-input {
  background: #F7F3EC;
  border: 1px solid rgba(27,59,46,0.15);
  border-radius: 8px;
  padding: 9px 12px;
  font-family: inherit;
  font-size: 14px;
  color: #1B3B2E;
  outline: none;
}
.ad-input:focus {
  border-color: #2B5E4A;
  box-shadow: 0 0 0 3px rgba(43,94,74,0.14);
}

.ad-primary {
  background: #1B3B2E;
  color: #FBF6E9;
  border: none;
  padding: 10px 22px;
  border-radius: 100px;
  font-family: inherit;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: opacity 0.15s;
}
.ad-primary:hover:not(:disabled) { opacity: 0.9; }
.ad-primary:disabled { opacity: 0.5; cursor: default; }

.ad-secondary {
  background: transparent;
  color: #1B3B2E;
  border: 1px solid rgba(27,59,46,0.22);
  padding: 8px 14px;
  border-radius: 100px;
  font-family: inherit;
  font-size: 12px;
  cursor: pointer;
}
.ad-secondary:hover { background: rgba(27,59,46,0.06); }

.ad-search {
  background: #F7F3EC;
  border: 1px solid rgba(27,59,46,0.15);
  border-radius: 100px;
  padding: 7px 14px;
  font-family: inherit;
  font-size: 12px;
  color: #1B3B2E;
  outline: none;
  min-width: 220px;
}

.ad-issue-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-bottom: 6px;
}
@media (max-width: 620px) {
  .ad-issue-grid { grid-template-columns: 1fr; }
}

.ad-row-header {
  display: flex; justify-content: space-between; align-items: center;
  margin-bottom: 16px;
  gap: 10px;
  flex-wrap: wrap;
}
.ad-actions { display: flex; gap: 8px; align-items: center; }

.ad-table {
  border-top: 1px solid rgba(27,59,46,0.08);
}
.ad-thead, .ad-trow {
  display: grid;
  grid-template-columns: 2fr 2fr 1fr 1fr 130px;
  gap: 12px;
  padding: 10px 6px;
  align-items: center;
  border-bottom: 1px solid rgba(27,59,46,0.06);
}
.ad-thead {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: #7A6947;
  font-weight: 500;
  padding: 12px 6px 10px;
}
.ad-trow { font-size: 13px; }

/* --- Mobile: collapse the fixed grid into stacked cards --- */
/* Below ~640px, the 5-column layout overflows the viewport and the
   right-most Revoke/Reinstate action gets clipped off-screen. Switch
   to a card layout where each field is its own labelled row, and the
   action button sits full-width at the bottom. */
@media (max-width: 640px) {
  .ad-thead { display: none; }
  .ad-table { border-top: none; }
  .ad-trow {
    display: block;
    padding: 14px 4px;
    border-bottom: 1px solid rgba(27,59,46,0.10);
  }
  .ad-trow > div {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 10px;
    padding: 3px 0;
  }
  .ad-trow > div::before {
    content: attr(data-label);
    font-size: 9.5px;
    color: #7A6947;
    text-transform: uppercase;
    letter-spacing: 1px;
    font-weight: 500;
    flex-shrink: 0;
  }
  .ad-trow > div:first-child::before { display: none; }
  .ad-trow > div:first-child {
    display: block;
    padding-bottom: 6px;
    border-bottom: 1px dashed rgba(27,59,46,0.10);
    margin-bottom: 4px;
  }
  .ad-truncate {
    text-align: right;
    white-space: normal;
    overflow: visible;
    word-break: break-word;
    max-width: 60%;
  }
  .ad-rowactions {
    justify-content: flex-end;
    padding-top: 6px !important;
  }
  .ad-rowactions::before { display: none !important; }

  /* Tighten container padding + let topbar wrap on narrow screens so
     nothing spills past the viewport edge. */
  .ad-root { padding: 14px 14px 60px; }
  .ad-panel { padding: 16px 16px; }
  .ad-topbar { flex-wrap: wrap; gap: 8px; }
  .ad-row-header { flex-direction: column; align-items: stretch; }
  .ad-actions { flex-direction: column; align-items: stretch; }
  .ad-search { min-width: 0; width: 100%; }
  .ad-issue-grid { grid-template-columns: 1fr; }
}
.ad-name { font-weight: 500; color: #1B3B2E; }
.ad-id {
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 10.5px;
  color: #9B8666;
  margin-top: 2px;
}
.ad-truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: #3d3021;
}
.ad-date { color: #7A6947; font-size: 12px; }
.ad-empty {
  padding: 30px;
  text-align: center;
  color: #7A6947;
  font-size: 13px;
}

.ad-badge {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 100px;
  font-size: 10.5px;
  font-weight: 500;
  letter-spacing: 0.03em;
  text-transform: uppercase;
}
.ad-badge-ok { background: rgba(43,94,74,0.14); color: #2B5E4A; }
.ad-badge-warn { background: rgba(202,132,20,0.14); color: #7A4E0E; }
.ad-badge-err { background: rgba(134,47,26,0.14); color: #862F1A; }

.ad-danger-link {
  background: transparent;
  border: none;
  color: #862F1A;
  font-family: inherit;
  font-size: 12px;
  cursor: pointer;
  padding: 4px 6px;
  border-radius: 6px;
}
.ad-danger-link:hover { background: rgba(134,47,26,0.08); }
.ad-danger-link-strong {
  font-weight: 600;
  border: 1px solid rgba(134,47,26,0.28);
  padding: 3px 10px;
  border-radius: 100px;
}
.ad-danger-link-strong:hover {
  background: rgba(134,47,26,0.10);
}
.ad-primary-link {
  background: transparent;
  border: none;
  color: #2B5E4A;
  font-family: inherit;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  padding: 4px 6px;
  border-radius: 6px;
}
.ad-primary-link:hover { background: rgba(43,94,74,0.08); }
.ad-rowactions {
  display: flex;
  gap: 4px;
  justify-content: flex-end;
  align-items: center;
}

.ad-error {
  background: rgba(134,47,26,0.08);
  border: 1px solid rgba(134,47,26,0.22);
  color: #862F1A;
  padding: 10px 14px;
  border-radius: 10px;
  font-size: 12.5px;
  margin-top: 10px;
  white-space: pre-line;
}

.ad-modal {
  position: fixed; inset: 0;
  background: rgba(15,40,30,0.55);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  z-index: 1000;
  display: flex; align-items: center; justify-content: center;
  padding: 20px;
}
.ad-modal-inner {
  background: #FFFDF7;
  border-radius: 20px;
  padding: 26px 30px;
  max-width: 460px;
  width: 100%;
  box-shadow: 0 20px 60px rgba(15,40,30,0.35);
}
.ad-modal-sub {
  font-size: 13px;
  color: #3d3021;
  line-height: 1.5;
  margin-bottom: 16px;
}
.ad-pin-box {
  border: 1px dashed rgba(43,94,74,0.35);
  background: rgba(43,94,74,0.05);
  border-radius: 14px;
  padding: 18px 22px;
  text-align: center;
  margin-bottom: 14px;
}
.ad-pin-label {
  font-size: 10px;
  color: #5D4A28;
  text-transform: uppercase;
  letter-spacing: 1.2px;
  margin-bottom: 6px;
}
.ad-pin-value {
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 24px;
  color: #1B3B2E;
  letter-spacing: 0.14em;
  font-weight: 600;
  margin-bottom: 12px;
  user-select: all;
  -webkit-user-select: all;
}
.ad-copy-btn {
  background: transparent;
  border: 1px solid rgba(27,59,46,0.22);
  color: #1B3B2E;
  font-family: inherit;
  font-size: 12px;
  padding: 6px 14px;
  border-radius: 100px;
  cursor: pointer;
}
.ad-copy-btn:hover { background: rgba(27,59,46,0.06); }
.ad-modal-meta {
  font-size: 11px;
  color: #7A6947;
  margin-bottom: 18px;
}
.ad-modal-meta code {
  font-family: 'Geist Mono', ui-monospace, monospace;
  background: rgba(93,74,40,0.06);
  padding: 1px 6px;
  border-radius: 4px;
}
`;
